import React from 'react';
import { Button } from '../../../shared/components/Button';
import '../../../shared/styles/Orchestrator.css';
import TokenList from './TokenList';
import LayerList from './LayerList';
import copyIcon from '../../../shared/assets/logos/copy.webp';
import Tooltip from '../../../shared/components/Tooltip';
import { toast } from 'react-toastify';
import BattleEffectList from './BattleEffectList';
import TrackerSection from './TrackerSection';
import { getSystemById } from '../data/ttrpgSystems';
import { useSelector, useDispatch } from 'react-redux';
import { toggleSidebarSticky } from '../../../store/slices/orchestratorSlice';
import {
  CirclePlusIcon,
  Grid2X2Plus,
  LayersPlus,
  Wand,
  Dices,
  Dice6,
  CircleArrowDown,
  ArrowDownToLine,
  ArrowUpFromLine,
  Trash2,
  CircleQuestionMark,
  RulerDimensionLine,
  BookOpen,
  Grid3X3,
  GalleryVertical,
  FlipVertical,
  Fullscreen,
  Locate,
  LocateOff,
  ZoomIn,
  ZoomOut,
  Map,
  Library,
  Pin,
  ChevronDown,
  Cloudy,
  MonitorPlay,
  View,
  SquarePlus,
  SquareArrowDown,
  LayersArrowDown,
} from 'lucide-react';

function ManagerSidebar({
  sessionToken,
  ttrpgSystemId = null,
  onSystemButtonClick,
  onGridConfig,
  onShowDisplay,
  isDisplayOpen = false,
  onAddToken,
  onAddLayer,
  onAddEffect,
  onShowDiceHistory,
  onShowGmDice,
  onDeleteScene,
  canDeleteScene,
  tokens,
  onToggleTokenVisible,
  onToggleTokenDeath,
  onToggleTokenDisposition,
  onEditToken,
  onToggleGroupTokensVisible,
  onReorderTokens,
  layers,
  onToggleLayer,
  onEditLayer,
  onToggleGroupLayersVisible,
  onStartLayerFlip,
  activeFlipLayerId,
  onReorderLayers,
  drawingLayerId,
  onToggleDrawLayer,
  onImportScene,
  onExportScene,
  onLoadTokens,
  battleEffects,
  onAddBattleEffect,
  onToggleBattleEffectVisible,
  onEditBattleEffect,
  onTogglePaintBattleEffect,
  onDeleteBattleEffect,
  onToggleGroupEffectsVisible,
  onReorderEffects,
  paintingBattleEffectId,
  showNamesMode = false,
  onToggleNamesMode,
  measuringMode = false,
  onToggleMeasuringMode,
  measurementDisabled = false,
  hasSelectedToken = false,
  selectedTokenId = null,
  mode = 'light',
  isZoomed = false,
  onToggleZoom,
  autoCenterOnSelect = true,
  onToggleAutoCenter,
  isDisplayReversed = false,
  onToggleDisplayReverse,
  allowPlayerControls = false,
  onTogglePlayerControls,
  trackers = [],
  onAddTracker,
  onEditTracker,
  onDeleteTracker,
  onToggleTrackerVisible,
  onChangeTrackerValue,
  onReorderTrackers,
  onToggleGroupTrackersVisible,
  onShowWorldMap,
  onShowLibrary,
  worldMapShared = false,
  worldMapDisplaying = false,
  libraryShared = false,
}) {
  const activeSystem = getSystemById(ttrpgSystemId);
  const isDaggerheart = ttrpgSystemId === 'daggerheart';
  const mapIsActive = worldMapShared || worldMapDisplaying;
  const mapDescription = worldMapShared && worldMapDisplaying
    ? 'Map (sharing, displaying)'
    : worldMapShared
      ? 'Map (Sharing)'
      : worldMapDisplaying
        ? 'Map (displaying)'
        : 'Map';
  const roleTitle = ttrpgSystemId === 'cota'
    ? 'Oracle'
    : ttrpgSystemId === 'coc7e'
      ? 'Keeper'
      : ['daggerheart', 'pf1e', 'pf2e', 'sf1e', 'sf2e'].includes(ttrpgSystemId)
        ? 'Game Master'
        : 'Dungeon Master';

  // Redux state
  const sidebarSticky = useSelector((state) => state.orchestrator?.sidebarSticky ?? true);
  const dispatch = useDispatch();

  // Sidebar layout detection - hide button when sidebar goes below manager (<= 1205px)
  const [isSidebarBelow, setIsSidebarBelow] = React.useState(false);

  React.useLayoutEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1205px)');
    const handleMediaChange = () => setIsSidebarBelow(mediaQuery.matches);
    handleMediaChange();
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  const copySessionTokenToClipboard = () => {
    if (!sessionToken) return;

    if (!navigator?.clipboard?.writeText) {
      toast.error('Clipboard is not available', {
        toastId: 'session-token-copy-unavailable',
      });
      return;
    }

    navigator.clipboard
      .writeText(sessionToken)
      .then(() => {
        toast.success('Player Session Token copied!', {
          toastId: 'session-token-copied',
        });
      })
      .catch(() => {
        toast.error('Failed to copy Player Session Token', {
          toastId: 'session-token-copy-failed',
        });
      });
  };

  return (
    <div
      className={`orchestrate_panel${mode === 'dark' ? ' orchestrate_panel_dark' : ''
        }${sidebarSticky ? '' : ' orchestrate_panel_non_sticky'}`}
      style={sidebarSticky ? { paddingBottom: '0px' } : {}}
    >
      {sessionToken && (
        <div style={{ marginBottom: 5 }}>
          <div className="orchestrator_section_header_first">
            <span
              className="orchestrator_section_title player_session_title"
              style={{ width: '235px' }}
            >
              Player Session
            </span>
            <Tooltip content="Click to copy">
              <span
                className="orchestrator_session_token"
                role="button"
                tabIndex={0}
                onClick={() => {
                  copySessionTokenToClipboard();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    copySessionTokenToClipboard();
                  }
                }}
              >
                <img
                  className={`orchestrator_session_token_icon ${mode === 'dark'
                    ? 'orchestrator_session_token_icon_dark'
                    : ''
                    }`}
                  src={copyIcon}
                  alt=""
                />
                <span className="orchestrator_session_token_text">
                  {sessionToken}
                </span>
              </span>
            </Tooltip>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          ></div>
        </div>
      )}
      <div className="manager_tool_sections">
        <details className="manager_tool_section" open>
          <summary className="manager_tool_header"><ChevronDown className="manager_tool_header_icon" size={14} /><span>{roleTitle}, Craft your world!</span></summary>
          <div className="manager_tool_buttons">
            <Button icon={activeSystem ? activeSystem.buttonIcon : BookOpen} description={activeSystem ? activeSystem.shortName : 'Select System'} type={activeSystem ? activeSystem.buttonType : 'cinnabar'} onPress={onSystemButtonClick} />
            <Button icon={Map} description={mapDescription} type={mapIsActive ? 'blue' : 'cinnabar'} onPress={onShowWorldMap} />
            <Button icon={Library} description={libraryShared ? 'Resources (sharing)' : 'Resources'} type={libraryShared ? 'blue' : 'cinnabar'} onPress={onShowLibrary} />

          </div>
        </details>
        <details className="manager_tool_section" open>
          <summary className="manager_tool_header"><ChevronDown className="manager_tool_header_icon" size={14} /><span>Make a scene!</span></summary>
          <div className="manager_tool_buttons">
            <Button icon={CirclePlusIcon} description="Add Token" type="cinnabar" onPress={onAddToken} />
            <Button icon={SquarePlus} description="Add Battle Effect" type="cinnabar" onPress={onAddBattleEffect} />
            <Button icon={LayersPlus} description="Add Layer" type="cinnabar" onPress={onAddLayer} />
            <Button icon={Grid3X3} description="Grid Size" type="cinnabar" onPress={onGridConfig} />
            <Button icon={Cloudy} description="Fog of War" type="disabled" disabled onPress={() => { }} />
            <Button icon={Wand} description="Effects" type="cinnabar" onPress={onAddEffect} />
            <Button icon={CircleArrowDown} description="Load Tokens" type="cinnabar" onPress={onLoadTokens} />
            <Button icon={SquareArrowDown} description="Load Battle Effects" type="disabled" disabled onPress={() => { }} />
            <Button icon={LayersArrowDown} description="Load Layers" type="disabled" disabled onPress={() => { }} />
            <Button icon={ArrowDownToLine} description="Import Scene" type="cinnabar" onPress={onImportScene} />
            <Button icon={ArrowUpFromLine} description="Export Scene" type="cinnabar" onPress={onExportScene} />
            {canDeleteScene && <Button icon={Trash2} description="Delete Scene" type="cinnabar" onPress={onDeleteScene} />}
          </div>
        </details>
        <details className="manager_tool_section" open>
          <summary className="manager_tool_header"><ChevronDown className="manager_tool_header_icon" size={14} /><span>Orchestration your Session!</span></summary>
          <div className="manager_tool_buttons">
            <Button icon={MonitorPlay} description="Orchestration" type="disabled" disabled onPress={() => { }} />
            <Button icon={Fullscreen} description={isDisplayOpen ? 'Refresh Display' : 'Open Display'} type={isDisplayOpen ? 'blue' : 'cinnabar'} onPress={onShowDisplay} />
            <Button icon={FlipVertical} description={isDisplayReversed ? 'Unflip Display' : 'Flip Display'} type={isDisplayReversed ? 'blue' : 'cinnabar'} onPress={onToggleDisplayReverse} />
            <Button icon={Dice6} description="Dice Roller" type="cinnabar" onPress={onShowGmDice} />
            <Button icon={Dices} description="Dice History" type="cinnabar" onPress={onShowDiceHistory} />
            <Button icon={isZoomed ? ZoomOut : ZoomIn} description="Zoom" type={isZoomed ? 'blue' : 'cinnabar'} onPress={onToggleZoom} />
            <Button icon={allowPlayerControls ? LocateOff : Locate} description={'Player Controls'} type={allowPlayerControls ? 'blue' : 'cinnabar'} onPress={onTogglePlayerControls} />
            <Button icon={CircleQuestionMark} description="Token Names" type={showNamesMode ? 'blue' : 'cinnabar'} onPress={onToggleNamesMode} />
            <Button icon={GalleryVertical} description="Follow Token" type={autoCenterOnSelect ? 'blue' : 'cinnabar'} onPress={onToggleAutoCenter} />
            <Button icon={View} description="Token PV" type="disabled" disabled onPress={() => { }} />
            <Button icon={RulerDimensionLine} description="Measurements" type={measuringMode ? 'blue' : 'cinnabar'} disabled={!hasSelectedToken || measurementDisabled} onPress={onToggleMeasuringMode} />
            {!isSidebarBelow && <Button icon={Pin} description={sidebarSticky ? 'Unstick from Top' : 'Stick to Top'} type={sidebarSticky ? 'blue' : 'cinnabar'} onPress={() => dispatch(toggleSidebarSticky())} />}
          </div>
        </details>
      </div>
      {sidebarSticky && (
        <div
          className={`sidebar_sticky_border${mode === 'dark' ? ' sidebar_sticky_border_dark' : ''}`}
        />
      )}
      {sidebarSticky ? (
        <div className="orchestrator_scrollable_content">
          {isDaggerheart && (
            <>
              <div
                className={`orchestrator_section_header${!sessionToken ? ' orchestrator_section_header_first' : ''
                  }`}
              >
                <span className="orchestrator_section_title">Trackers</span>
              </div>
              <TrackerSection
                trackers={trackers}
                mode={mode}
                onAddTracker={onAddTracker}
                onEditTracker={onEditTracker}
                onDeleteTracker={onDeleteTracker}
                onToggleTrackerVisible={onToggleTrackerVisible}
                onChangeTrackerValue={onChangeTrackerValue}
                onReorderTrackers={onReorderTrackers}
                onToggleGroupVisible={onToggleGroupTrackersVisible}
              />
            </>
          )}

          <div
            className={`orchestrator_section_header${!sessionToken && !isDaggerheart
              ? ' orchestrator_section_header_first'
              : ''
              }`}
          >
            <span className="orchestrator_section_title">Tokens</span>
          </div>
          <TokenList
            tokens={tokens}
            selectedTokenId={selectedTokenId}
            onToggleVisible={onToggleTokenVisible}
            onToggleDeath={onToggleTokenDeath}
            onToggleDisposition={onToggleTokenDisposition}
            onEdit={onEditToken}
            onToggleGroupVisible={onToggleGroupTokensVisible}
            onReorder={onReorderTokens}
          />

          <div className="orchestrator_section_header">
            <span className="orchestrator_section_title">Battle Effects</span>
          </div>
          <BattleEffectList
            effects={battleEffects}
            paintingId={paintingBattleEffectId}
            onToggleVisible={onToggleBattleEffectVisible}
            onEditMeta={onEditBattleEffect}
            onTogglePaint={onTogglePaintBattleEffect}
            onDelete={onDeleteBattleEffect}
            onToggleGroupVisible={onToggleGroupEffectsVisible}
            onReorder={onReorderEffects}
          />

          <div className="orchestrator_section_header">
            <span className="orchestrator_section_title">Layers</span>
          </div>
          <LayerList
            layers={layers}
            onToggleVisible={onToggleLayer}
            onEdit={onEditLayer}
            onToggleGroupVisible={onToggleGroupLayersVisible}
            onStartFlip={onStartLayerFlip}
            activeFlipLayerId={activeFlipLayerId}
            onReorder={onReorderLayers}
            drawingLayerId={drawingLayerId}
            onToggleDraw={onToggleDrawLayer}
          />
        </div>
      ) : (
        <>
          {isDaggerheart && (
            <>
              <div
                className={`orchestrator_section_header${!sessionToken ? ' orchestrator_section_header_first' : ''
                  }`}
              >
                <span className="orchestrator_section_title">Trackers</span>
              </div>
              <TrackerSection
                trackers={trackers}
                mode={mode}
                onAddTracker={onAddTracker}
                onEditTracker={onEditTracker}
                onDeleteTracker={onDeleteTracker}
                onToggleTrackerVisible={onToggleTrackerVisible}
                onChangeTrackerValue={onChangeTrackerValue}
                onReorderTrackers={onReorderTrackers}
                onToggleGroupVisible={onToggleGroupTrackersVisible}
              />
            </>
          )}

          <div
            className={`orchestrator_section_header${!sessionToken && !isDaggerheart
              ? ' orchestrator_section_header_first'
              : ''
              }`}
          >
            <span className="orchestrator_section_title">Tokens</span>
          </div>
          <TokenList
            tokens={tokens}
            selectedTokenId={selectedTokenId}
            onToggleVisible={onToggleTokenVisible}
            onToggleDeath={onToggleTokenDeath}
            onToggleDisposition={onToggleTokenDisposition}
            onEdit={onEditToken}
            onToggleGroupVisible={onToggleGroupTokensVisible}
            onReorder={onReorderTokens}
          />
          <div className="orchestrator_section_header">
            <span className="orchestrator_section_title">Battle Effects</span>
          </div>
          <BattleEffectList
            effects={battleEffects}
            paintingId={paintingBattleEffectId}
            onToggleVisible={onToggleBattleEffectVisible}
            onEditMeta={onEditBattleEffect}
            onTogglePaint={onTogglePaintBattleEffect}
            onDelete={onDeleteBattleEffect}
            onToggleGroupVisible={onToggleGroupEffectsVisible}
            onReorder={onReorderEffects}
          />

          <div className="orchestrator_section_header">
            <span className="orchestrator_section_title">Layers</span>
          </div>
          <LayerList
            layers={layers}
            onToggleVisible={onToggleLayer}
            onEdit={onEditLayer}
            onToggleGroupVisible={onToggleGroupLayersVisible}
            onStartFlip={onStartLayerFlip}
            activeFlipLayerId={activeFlipLayerId}
            onReorder={onReorderLayers}
            drawingLayerId={drawingLayerId}
            onToggleDraw={onToggleDrawLayer}
          />
        </>
      )}
    </div>
  );
}

export default ManagerSidebar;
